import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import moment from 'moment';
import {ToastrService} from 'ngx-toastr';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import QPTypeModel from 'src/app/services/qpType.model';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';
import { QPTypeService } from 'src/app/services/qp-type.service';

declare let $: any;

@Component({
  selector: 'app-quality-parameter-types',
  templateUrl: './quality-parameter-types.component.html',
  styleUrls: ['./quality-parameter-types.component.scss']
})
export class QualityParameterTypesComponent implements OnInit, OnDestroy {
  public qpTypes: QPTypeModel[] = [];
  public qpTypesList: QPTypeModel[] = [];
  public qpType: QPTypeModel = new QPTypeModel();
  public filteredQpTypes: QPTypeModel[] = [];
  public search: string;
  isAddClick = false;
  registerForm: FormGroup;
  submitted = false;
  public submitButton: boolean;
  public updateButton: boolean;
  public qpTypesDoubleClick: boolean;
  public NoDataMessage: boolean = false;
  public clearSearchInput: boolean;
  public enableDisablePageLoader: boolean = false;
  public listLoader = false;              // Show listLoader spinner
  public startIndex = 0;             // Tracks how much has been loaded
  public isFiltering: boolean = false;
  public noList: boolean = false;
  searchTimeout: any;
  public formFieldCount: number = 0;
  public showForm: boolean = false;

  constructor(private toastrService: ToastrService, public formBuilder: FormBuilder, public qpTypeService: QPTypeService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.qpType = new QPTypeModel();
    /*form validation*/
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      abbreviation: ['', [Validators.required, Validators.maxLength(10), Validators.pattern('^[a-zA-Z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  private batchLoader: BatchLoader<any>;

  public async ngOnInit() {
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.qpTypes = [];
    this.qpTypesList = [];
    this.filteredQpTypes = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.qpTypes = await this.qpTypeService.getAllQpTypes();
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.enableDisablePageLoader = false;
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;

  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredQpTypes : this.qpTypes,
      (updatedList) => {
        this.qpTypesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  //To cancel ongoing requests and unsubscribe from subscriptions.
  public ngOnDestroy(): void {
    this.qpTypeService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader.reset();
  }

  /*// qp type register form convenience getter for easy access to form fields*/
  get qpTypeFormValidation() {
    return this.registerForm.controls;
  }

  // search functionality
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.qpTypes.filter(qptype =>
        qptype.name?.toLowerCase().includes(query) ||
        qptype.abbreviation?.toLowerCase().includes(query) ||
        qptype.description?.toLowerCase().includes(query)
      )
      : this.qpTypes;
    this.filteredQpTypes = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.qpTypesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.qpType = new QPTypeModel();
    this.clearSearchInput = false;
    this.submitButton = false;
    this.updateButton = false;
    this.qpTypesDoubleClick = false;
    this.submitted = false;
  }

  /*Add qp Type*/
  public async addUpdateQPType() {
    this.isAddClick = true;
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    // GET FORM VALUES
    const formData = this.registerForm.value; // contains updated values
    try {
      this.qpTypesDoubleClick = true;
      if (this.submitButton) {
        await this.qpTypeService.create(formData);
        this.toastrService.success('Qp type created successfully', '', {
          timeOut: 2000
        });
      } else {
        const updateQpType = {...formData}
        delete updateQpType.id;
        await this.qpTypeService.updateOneByID(this.qpType.id, updateQpType);
        this.toastrService.success('Qp type updated successfully', '', {
          timeOut: 2000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.toastrService.error(error?.error?.message || error?.message || error);
      this.qpTypesDoubleClick = false;
    }
  }

  /*Get qp type by id*/
  public getQPTypeById(id: string) {
    this.showForm = true;
    this.updateButton = true;
    this.submitButton = false;
    this.qpTypesDoubleClick = false;
    const foundQpType = this.qpTypesList.find(uom => uom?.id === id);
    this.qpType = foundQpType ? {...foundQpType} : new QPTypeModel(); // clone object
    if (this.qpType) {
      this.registerForm.patchValue({
        name: this.qpType.name,
        abbreviation: this.qpType.abbreviation,
        description: this.qpType.description
      });
    }
  }

  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
